import React, { useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { 
  Upload, 
  FileText, 
  Image as ImageIcon, 
  Camera,
  Plus
} from 'lucide-react';

/**
 * InspectionUploadDropzone - Drag and drop upload zone
 * 
 * Supports:
 * - Drag/drop
 * - Multi upload
 * - Mobile camera capture
 * - PDF, JPG, JPEG, PNG, HEIC
 */
export default function InspectionUploadDropzone({ onFilesAdded }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Accepted file types
  const acceptedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/heic',
    'image/heif'
  ];
  
  const acceptString = '.pdf,.jpg,.jpeg,.png,.heic,.heif';
  
  // Validate and filter files
  const processFiles = useCallback((fileList) => {
    const validFiles = [];
    const errors = [];
    
    Array.from(fileList).forEach(file => {
      // Check type
      const isValidType = acceptedTypes.some(type => 
        file.type === type || 
        file.name.toLowerCase().endsWith(type.split('/')[1])
      );
      
      // Check size (25MB max)
      const isValidSize = file.size <= 25 * 1024 * 1024;
      
      if (!isValidType) {
        errors.push(`${file.name}: Unsupported file type`);
      } else if (!isValidSize) {
        errors.push(`${file.name}: File too large (max 25MB)`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (errors.length > 0) {
      console.warn('File validation errors:', errors);
    }
    
    if (validFiles.length > 0) {
      onFilesAdded(validFiles);
    }
  }, [onFilesAdded]);
  
  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);
  
  // Handle file input change
  const handleFileSelect = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input
    e.target.value = '';
  }, [processFiles]);
  
  // Click handlers
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };
  
  return (
    <div className="space-y-4">
      {/* Main dropzone */}
      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-all duration-200 hover:border-blue-400 hover:bg-gray-50"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        data-testid="upload-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptString}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-full">
              <Upload className="h-6 w-6 text-blue-600" />
            </div>
            <div className="p-3 bg-purple-50 rounded-full">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <ImageIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          
          <div>
            <p className="text-lg font-medium text-gray-700">
              Drop inspection sheets here
            </p>
            <p className="text-sm text-gray-500 mt-1">
              or click to browse files
            </p>
          </div>
          
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>PDF</span>
            <span>•</span>
            <span>JPG</span>
            <span>•</span>
            <span>PNG</span>
            <span>•</span>
            <span>HEIC</span>
            <span className="ml-2">Max 25MB each</span>
          </div>
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex items-center justify-center gap-3">
        <Button 
          variant="outline" 
          onClick={handleBrowseClick}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Browse Files
        </Button>
        
        {/* Camera capture for mobile */}
        <Button 
          variant="outline" 
          onClick={handleCameraClick}
          className="flex items-center gap-2"
        >
          <Camera className="h-4 w-4" />
          Take Photo
        </Button>
        
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    </div>
  );
}
